// ===========================================================================
// AnalysisTab — Tab 2 del módulo. Panorama de la población; el costo domina.
// Tiene su propia barra de filtros (Location + PCP) que se propaga a los 4
// reportes. Cada reporte se carga de forma independiente: si uno falla/404
// (los SP de reporte figuran "por construir" en la doc), su panel muestra
// "no data" sin tumbar los demás.
// ===========================================================================
import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, MenuItem, FormControlLabel, Switch } from "@mui/material";
import {
  getDashboardKpis,
  getByHealthStatus,
  getCostComposition,
  getSocialDistribution,
} from "../../services";
import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";
import CostHeroBlock from "./CostHeroBlock";
import CostCompositionChart from "./CostCompositionChart";
import HealthStatusCohort from "./HealthStatusCohort";
import SocialDistributionRow from "./SocialDistributionRow";

export default function AnalysisTab() {
  const [locations, setLocations] = useState([]);
  const [pcps, setPcps] = useState([]);
  const [filters, setFilters] = useState({ locationId: "", pcpName: "", onlyActive: true });

  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [composition, setComposition] = useState([]);
  const [cohort, setCohort] = useState([]);
  const [social, setSocial] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [locs, pcpList] = await Promise.all([getLocations({ status: 1 }), getPcps({ status: 1 })]);
        if (!active) return;
        setLocations(Array.isArray(locs) ? locs : []);
        setPcps(Array.isArray(pcpList) ? pcpList : []);
      } catch {
        /* dropdowns opcionales */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const apiFilters = useMemo(
    () => ({
      locationId: filters.locationId || null,
      pcpName: filters.pcpName || null,
      onlyActive: filters.onlyActive,
    }),
    [filters]
  );

  useEffect(() => {
    let active = true;
    setKpisLoading(true);
    (async () => {
      const results = await Promise.allSettled([
        getDashboardKpis(apiFilters),
        getCostComposition(apiFilters),
        getByHealthStatus(apiFilters),
        getSocialDistribution(apiFilters),
      ]);
      if (!active) return;
      setKpis(results[0].status === "fulfilled" ? results[0].value : null);
      setComposition(results[1].status === "fulfilled" ? results[1].value : []);
      setCohort(results[2].status === "fulfilled" ? results[2].value : []);
      setSocial(results[3].status === "fulfilled" ? results[3].value : []);
      setKpisLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [apiFilters]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Box>
      {/* Filtros del análisis */}
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2.5 }}>
        <TextField select size="small" label="Location" value={filters.locationId} onChange={set("locationId")} sx={{ width: 220 }}>
          <MenuItem value="">All locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.locationId} value={loc.locationId}>
              {loc.medicalCenter}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="PCP" value={filters.pcpName} onChange={set("pcpName")} sx={{ width: 220 }}>
          <MenuItem value="">All PCPs</MenuItem>
          {pcps.map((p) => (
            <MenuItem key={p.employeePcpId} value={p.pcpFullName}>
              {p.pcpFullName}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch size="small" checked={filters.onlyActive} onChange={(e) => setFilters((f) => ({ ...f, onlyActive: e.target.checked }))} />}
          label="Only active"
        />
      </Stack>

      {/* Bloque protagonista */}
      <Box sx={{ mb: 2.5 }}>
        <CostHeroBlock kpis={kpis} loading={kpisLoading} />
      </Box>

      {/* Fila de reportes: composición (2/3) + cohorte clínica (1/3) */}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          mb: 2.5,
        }}
      >
        <CostCompositionChart data={composition} />
        <HealthStatusCohort data={cohort} />
      </Box>

      {/* Capa social */}
      <SocialDistributionRow data={social} />
    </Box>
  );
}
